/**
 * Review before the RFQs go out.
 *
 * Sending an RFQ is the first irreversible step in the flow — a supplier reads it, and a
 * wrong quantity or a missing line comes back as a wrong quote days later. So the split gets
 * shown before it is sent, not after.
 *
 * Two levels in one dialog:
 *   LIST   — every supplier with the exact lines they will be asked to price. This is the
 *            whole review; nothing has to be opened for the send to be correct.
 *   DETAIL — one supplier's covering mail: To, Cc, Subject and a note, all prefilled, plus
 *            the same item table read-only.
 *
 * The primary action lives on the list and sends every RFQ individually in one click, because
 * the common case is "this is right, send it". Opening a supplier is for the case where it
 * isn't.
 *
 * The item table is NOT editable anywhere here. It comes from triage, and the protected Excel
 * is generated from the same lines — letting someone retype a quantity into the mail would
 * put the sheet and the covering table into disagreement, and the supplier would be right to
 * price either one.
 */

import { useToast } from '../../components/Toast'
import { useSyncSuppliersFromErp } from '../../lib/api/erpSync'
import { lookupSupplierAddresses } from '../../lib/api/supplierContacts'
import { useState } from 'react'
import { Badge } from '../../components/Badge'
import { Modal } from '../../components/Modal'
import { Button } from '../../components/ui'
import { RFQ_FROM, draftsWithoutRecipient, parseAddresses, recipientFills, rfqDescription, type RfqDraft } from '../../lib/rfq'
import type { Item } from '../../lib/types'

function ItemTable({ draft, itemsByCode }: { draft: RfqDraft; itemsByCode: Map<string, Item> }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      {draft.alreadySent ? (
        <div className="banner" style={{ marginBottom: 8 }}>
          {/* A supplementary RFQ (Akhil, 2026-09-15): what they already have is not re-sent. */}
          📨 <b>{draft.supplierName}</b> already has <b>{draft.alreadySent.lines.length}</b> line
          {draft.alreadySent.lines.length === 1 ? '' : 's'} on <span className="mono">{draft.alreadySent.rfq}</span>
          {' '}(#{draft.alreadySent.lines.map((l) => l.cust_sno).join(', #')}). This sends a <b>new RFQ</b> with only the{' '}
          {draft.lines.length} line{draft.lines.length === 1 ? '' : 's'} below — the sent sheet is not changed.
        </div>
      ) : null}
      <table>
        <thead>
          <tr>
            <th>Cust. S.No</th>
            <th>Item code</th>
            <th>Description</th>
            <th className="right">Qty</th>
            <th>UOM</th>
            <th className="right">Unit price</th>
          </tr>
        </thead>
        <tbody>
          {draft.lines.map((line) => {
            const master = itemsByCode.get(line.item_code ?? '')?.name
            const printed = rfqDescription(line, master)
            return (
            <tr key={line.line_id}>
              <td className="mono">{line.cust_sno}</td>
              <td className="mono">{line.item_code}</td>
              <td>
                {/* Exactly what the RFQ row will print — see `buildRfqItemRows`. */}
                <div>{printed}</div>
                {/* The catalogue name, for us, when the sheet prints something else. */}
                {master && master !== printed ? (
                  <div style={{ color: 'var(--muted)', fontSize: 11 }}>{master}</div>
                ) : null}
              </td>
              <td className="right mono">{line.qty}</td>
              <td>{line.uom}</td>
              <td className="right" style={{ color: 'var(--muted)' }}>
                — to be filled by supplier —
              </td>
            </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export function SendRfqModal({
  drafts,
  itemsByCode,
  enquiryId,
  sending,
  onChange,
  onSend,
  onClose,
}: {
  drafts: RfqDraft[]
  itemsByCode: Map<string, Item>
  enquiryId: string
  sending: boolean
  onChange: (supplier: string, patch: Partial<RfqDraft>) => void
  onSend: () => void
  onClose: () => void
}) {
  // A single supplier has nothing to choose between, so it opens on its own mail.
  const [openSupplier, setOpenSupplier] = useState<string | null>(drafts.length === 1 ? drafts[0].supplier : null)

  const active = drafts.find((d) => d.supplier === openSupplier) ?? null
  const missing = draftsWithoutRecipient(drafts)
  /*
    "Sync supplier emails from ERP" (2026-10-26): each supplier and its contacts are pulled from
    the ERP, the addresses looked up again, and a draft that had none takes the one now found.
    An address already on a draft — resolved or typed — is left as it is.
  */
  const toast = useToast()
  const [syncing, setSyncing] = useState(false)
  const syncSuppliers = useSyncSuppliersFromErp()
  const syncAddresses = async () => {
    if (syncing) return
    setSyncing(true)
    try {
      const result = await syncSuppliers.mutateAsync(drafts.map((d) => d.supplier))
      const found = new Map<string, string[]>()
      await Promise.all(
        drafts.map(async (d) => {
          const addresses = await lookupSupplierAddresses(d.supplier).catch(() => null)
          if (addresses) found.set(d.supplier, addresses.to)
        }),
      )
      const fills = recipientFills(drafts, found)
      for (const fill of fills) onChange(fill.supplier, { to: fill.to })
      const stillMissing = missing.length - fills.length
      toast(
        `Synced ${result.codes.length} supplier${result.codes.length === 1 ? '' : 's'} from the ERP` +
          (fills.length > 0 ? ` — ${fills.length} address${fills.length === 1 ? '' : 'es'} filled in` : '') +
          (stillMissing > 0 ? `; ${stillMissing} still ha${stillMissing === 1 ? 's' : 've'} no email in the ERP` : '') +
          (result.failed.length > 0 ? `. Refused: ${result.failed.slice(0, 2).join('; ')}` : '.'),
      )
    } catch (error) {
      toast(`Supplier sync failed — ${error instanceof Error ? error.message : String(error)}`)
    } finally {
      setSyncing(false)
    }
  }
  const syncButton = (
    <button type="button" className="linkish" disabled={syncing || drafts.length === 0} onClick={() => void syncAddresses()}
      title="Pull each supplier's latest email address from the ERP">
      {syncing ? 'syncing supplier emails…' : '↻ Sync supplier emails from ERP'}
    </button>
  )
  const lineCount = drafts.reduce((n, d) => n + d.lines.length, 0)

  const sendButton = (
    <Button
      variant="primary"
      disabled={sending || drafts.length === 0 || missing.length > 0}
      title={
        missing.length > 0
          ? `${missing.map((d) => d.supplierName).join(', ')} ${missing.length === 1 ? 'has' : 'have'} no email address`
          : undefined
      }
      onClick={onSend}
    >
      {sending ? 'Sending…' : `➤ Send ${drafts.length} RFQ${drafts.length === 1 ? '' : 's'}`}
    </Button>
  )

  return (
    <Modal
      title={
        active ? (
          <span>
            ✉️ RFQ to <b>{active.supplierName}</b>
          </span>
        ) : (
          <span>
            ⚙️ Send {drafts.length} RFQ{drafts.length === 1 ? '' : 's'} · {enquiryId}
          </span>
        )
      }
      onClose={onClose}
      // Wide enough for the item table without horizontal scrolling on a laptop.
      width={980}
      footer={
        <>
          {/* Back only exists when there is a list to go back to. */}
          {active && drafts.length > 1 ? (
            <Button onClick={() => setOpenSupplier(null)}>← All suppliers</Button>
          ) : null}
          {sendButton}
          <span style={{ flex: 1 }} />
          <Button onClick={onClose}>Cancel</Button>
        </>
      }
    >
      <div className="mb">
        {active ? (
          <div className="pad" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div className="crow">
              <label>From</label>
              {/* Fixed: replies have to land in the supplier mailbox to thread back. */}
              <input value={RFQ_FROM} readOnly title="RFQs are sent from the supplier mailbox" />
            </div>

            <div className="crow">
              <label htmlFor="rfq-to">
                To
              </label>
              <input
                id="rfq-to"
                value={active.to}
                placeholder="supplier@example.com, second@example.com"
                onChange={(e) => onChange(active.supplier, { to: e.target.value })}
              />
            </div>

            <div className="crow">
              <label htmlFor="rfq-cc">
                Cc
              </label>
              <input
                id="rfq-cc"
                value={active.cc}
                placeholder="comma-separated"
                onChange={(e) => onChange(active.supplier, { cc: e.target.value })}
              />
            </div>

            {/* Read-only, because the SERVER composes the subject now and ours is not sent.
                Its subject carries the RFQ's `req_tag`, and that tag is what makes the supplier's
                reply link itself back to this RFQ — measured live 2026-08-08: a reply to a
                server-composed subject came back linked; replies to our own `[ENQ-…]` subjects
                came back unlinked, twice. Leaving this editable would let a purchaser change a
                value that is silently discarded, and the change they most wanted to make (keeping
                the tag) is the one the server already guarantees. */}
            <div className="crow">
              <label htmlFor="rfq-subject">Subject</label>
              <input id="rfq-subject" value="Set by the server — carries the RFQ tag replies thread on" disabled />
            </div>



            <label style={{ fontSize: 12, color: 'var(--muted)' }} htmlFor="rfq-note">
              Covering note — add anything this supplier needs to know (delivery date, port, packing)
            </label>
            <textarea
              id="rfq-note"
              className="ceditor"
              style={{ minHeight: 150 }}
              value={active.note}
              onChange={(e) => onChange(active.supplier, { note: e.target.value })}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span className="cchip">
                <span style={{ fontSize: 16 }}>📊</span>
                <span>RFQ template .xlsx</span>
                <span style={{ color: 'var(--muted)' }}>generated on send</span>
              </span>
              {/* A PREVIEW. We send only the note; the server builds the sheet from the RFQ's own
                  rows and, for now, also prints them as a table under the note (asks file, E23).
                  The old wording — "appended to the mail" — read as if this screen did that. */}
              <span style={{ color: 'var(--muted)', fontSize: 12 }}>
                Preview of the lines on this RFQ — the sheet is built from exactly these on the server. Not editable here.
              </span>
            </div>

            <ItemTable draft={active} itemsByCode={itemsByCode} />
          </div>
        ) : (
          <>
            <div className="pad" style={{ color: 'var(--muted)', fontSize: 12.5, borderBottom: '1px solid var(--line)' }}>
              One RFQ per supplier, {lineCount} line{lineCount === 1 ? '' : 's'} in total. A line assigned to several
              suppliers appears in each of their RFQs as a competing quote. Everything below is ready to send —
              open a supplier only to change its addressing or add a note.
              {missing.length > 0 ? (
                <div style={{ marginTop: 6 }}>
                  <Badge tone="danger">
                    ⚠ No email address could be resolved for {missing.map((d) => d.supplierName).join(', ')} — open the
                    supplier and enter one below, or fix it in the supplier record, before sending
                  </Badge>{' '}
                  {syncButton}
                </div>
              ) : (
                <div style={{ marginTop: 6 }}>{syncButton}</div>
              )}
            </div>

            {drafts.map((draft) => (
              <div key={draft.supplier} style={{ borderBottom: '1px solid var(--line)' }}>
                <div
                  className="pad"
                  role="button"
                  tabIndex={0}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', flexWrap: 'wrap' }}
                  onClick={() => setOpenSupplier(draft.supplier)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setOpenSupplier(draft.supplier)
                    }
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 800 }}>{draft.supplierName}</div>
                    <div style={{ color: 'var(--muted)', fontSize: 11.5 }}>
                      {parseAddresses(draft.to).length > 0 ? (
                        parseAddresses(draft.to).join(', ')
                      ) : (
                        <b style={{ color: 'var(--danger)' }}>no address</b>
                      )}
                      {parseAddresses(draft.cc).length > 0 ? ` · cc ${parseAddresses(draft.cc).join(', ')}` : ''}
                    </div>
                  </div>
                  <Badge tone="info">
                    {draft.lines.length} line{draft.lines.length === 1 ? '' : 's'}
                  </Badge>
                  {/* An edited note is worth seeing from the list — it is the one thing that
                      differs between otherwise identical mails. */}
                  {draft.note.trim() ? <Badge tone="muted">note added</Badge> : null}
                  <span style={{ flex: 1 }} />
                  <span style={{ color: 'var(--brand)', fontWeight: 700, fontSize: 12 }}>edit mail →</span>
                </div>

                <div className="pad" style={{ paddingTop: 0 }}>
                  <ItemTable draft={draft} itemsByCode={itemsByCode} />
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </Modal>
  )
}
